import * as React from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { cn } from "@/utils/utils"

const DAY_LABELS  = ['Su','Mo','Tu','We','Th','Fr','Sa']
const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December']

const today = new Date()
today.setHours(0,0,0,0)

const sameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth()    === b.getMonth()    &&
  a.getDate()     === b.getDate()

function buildCells(year, month) {
  const firstDay    = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const prevDays    = new Date(year, month, 0).getDate()
  const cells       = []

  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({ date: new Date(year, month - 1, prevDays - i), outside: true })

  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false })

  while (cells.length % 7 !== 0)
    cells.push({ date: new Date(year, month + 1, cells.length - daysInMonth - firstDay + 1), outside: true })

  return cells
}

/* ── Month/Year Picker ───────────────────────────────────────────────────── */
function MonthYearPicker({ year, month, onChange, onClose }) {
  const currentYear = new Date().getFullYear()
  const [viewYear, setViewYear]     = React.useState(year)
  const [yearPickerOpen, setYearPickerOpen] = React.useState(false)

  // Years range: 10 before and 10 after current year
  const years = React.useMemo(
    () => Array.from({ length: 21 }, (_, i) => currentYear - 10 + i),
    [currentYear]
  )

  const btnBase = {
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'background 0.15s',
    fontFamily: 'inherit',
  }

  return (
    /* Fixed full-screen overlay */
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Card */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 320,
          background: 'linear-gradient(160deg, #14532d 0%, #1c1917 55%, #0f172a 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 20,
          padding: '16px 16px 20px',
          boxShadow: '0 24px 48px rgba(0,0,0,0.55)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: '#fff', letterSpacing: '0.02em' }}>
            {yearPickerOpen ? 'Select Year' : 'Select Month & Year'}
          </span>
          <button
            onClick={onClose}
            style={{
              ...btnBase,
              background: 'rgba(255,255,255,0.10)',
              color: 'rgba(255,255,255,0.75)',
              padding: '5px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '50%',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.20)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {yearPickerOpen ? (
          /* ── Year grid ── */
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 6,
              maxHeight: 240,
              overflowY: 'auto',
            }}>
              {years.map(y => {
                const isActive = y === viewYear
                return (
                  <button
                    key={y}
                    onClick={() => {
                      setViewYear(y)
                      setYearPickerOpen(false)
                    }}
                    style={{
                      ...btnBase,
                      padding: '10px 4px',
                      fontSize: '0.78rem',
                      fontWeight: isActive ? 700 : 500,
                      background: isActive ? '#16a34a' : 'rgba(255,255,255,0.07)',
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.75)',
                      border: isActive
                        ? '1px solid rgba(255,255,255,0.25)'
                        : '1px solid rgba(255,255,255,0.05)',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.14)'
                        e.currentTarget.style.color = '#ffffff'
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.07)'
                        e.currentTarget.style.color = 'rgba(255,255,255,0.75)'
                      }
                    }}
                  >
                    {y}
                  </button>
                )
              })}
            </div>
            <button
              onClick={() => setYearPickerOpen(false)}
              style={{
                ...btnBase,
                marginTop: 12,
                width: '100%',
                padding: '8px',
                fontSize: '0.8rem',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.55)',
                textAlign: 'center',
              }}
            >
              ← Back to months
            </button>
          </div>
        ) : (
          /* ── Month view ── */
          <div>
            {/* Year row — click year to open year picker */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
            }}>
              <button
                onClick={() => setYearPickerOpen(true)}
                style={{
                  ...btnBase,
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#ffffff',
                  padding: '8px 20px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.10)'}
              >
                {viewYear}
                <span style={{ fontSize: '0.6rem', opacity: 0.6 }}>▼</span>
              </button>
            </div>

            {/* Month grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {MONTH_NAMES.map((name, i) => {
                const isActive = i === month && viewYear === year
                return (
                  <button
                    key={name}
                    onClick={() => { onChange(viewYear, i); onClose() }}
                    style={{
                      ...btnBase,
                      padding: '12px 4px',
                      fontSize: '0.78rem',
                      fontWeight: isActive ? 700 : 500,
                      background: isActive ? '#16a34a' : 'rgba(255,255,255,0.07)',
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.75)',
                      border: isActive
                        ? '1px solid rgba(255,255,255,0.25)'
                        : '1px solid rgba(255,255,255,0.05)',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.14)'
                        e.currentTarget.style.color = '#ffffff'
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.07)'
                        e.currentTarget.style.color = 'rgba(255,255,255,0.75)'
                      }
                    }}
                  >
                    {name.slice(0, 3)}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Calendar ────────────────────────────────────────────────────────────── */
function Calendar({
  className,
  selected,
  onSelect,
  month: controlledMonth,
  onMonthChange,
  defaultMonth,
  showOutsideDays = true,
  components,
  mode = 'single',
  ...props
}) {
  const initial = controlledMonth ?? defaultMonth ?? (selected instanceof Date ? selected : today)
  const [viewYear,   setViewYear]   = React.useState(initial.getFullYear())
  const [viewMonth,  setViewMonth]  = React.useState(initial.getMonth())
  const [pickerOpen, setPickerOpen] = React.useState(false)

  // Sync if controlled month changes externally
  React.useEffect(() => {
    if (controlledMonth) {
      setViewYear(controlledMonth.getFullYear())
      setViewMonth(controlledMonth.getMonth())
    }
  }, [controlledMonth])

  const navigate = (dir) => {
    let y = viewYear, m = viewMonth + dir
    if (m > 11) { m = 0;  y++ }
    if (m < 0)  { m = 11; y-- }
    setViewYear(y)
    setViewMonth(m)
    onMonthChange?.(new Date(y, m, 1))
  }

  const handlePickerChange = (y, m) => {
    setViewYear(y)
    setViewMonth(m)
    onMonthChange?.(new Date(y, m, 1))
  }

  const cells = React.useMemo(() => buildCells(viewYear, viewMonth), [viewYear, viewMonth])
  const ExternalDayContent = components?.DayContent

  return (
    <div
      className={cn("w-full select-none", className)}
      style={{ position: 'relative', fontFamily: 'inherit' }}
      {...props}
    >
      {/* ── MonthYearPicker overlay (portal-style fixed) ── */}
      {pickerOpen && (
        <MonthYearPicker
          year={viewYear}
          month={viewMonth}
          onChange={handlePickerChange}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px 12px',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: '1px solid hsl(var(--border))',
            borderRadius: 8, cursor: 'pointer', padding: '4px 8px',
            display: 'flex', alignItems: 'center', color: 'inherit', opacity: 0.7,
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
        >
          <ChevronLeft style={{ width: 16, height: 16 }} />
        </button>

        {/* Month/year label — click to open picker */}
        <button
          onClick={() => setPickerOpen(v => !v)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '0.95rem', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 4, color: 'inherit',
            padding: '4px 8px', borderRadius: 8, transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--accent))'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          {MONTH_NAMES[viewMonth]} {viewYear}
          <span style={{
            fontSize: '0.55rem', opacity: 0.5, display: 'inline-block',
            transform: pickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}>▼</span>
        </button>

        <button
          onClick={() => navigate(1)}
          style={{
            background: 'none', border: '1px solid hsl(var(--border))',
            borderRadius: 8, cursor: 'pointer', padding: '4px 8px',
            display: 'flex', alignItems: 'center', color: 'inherit', opacity: 0.7,
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
        >
          <ChevronRight style={{ width: 16, height: 16 }} />
        </button>
      </div>

      {/* ── Day labels ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        width: '100%',
        padding: '0 4px',
      }}>
        {DAY_LABELS.map((d, i) => (
          <div key={d} style={{
            textAlign: 'center',
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '4px 0 8px',
            color: i === 0 ? '#ef4444' : 'hsl(var(--muted-foreground))',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* ── Day grid ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        width: '100%',
        padding: '0 4px 8px',
        gap: '2px 0',
      }}>
        {cells.map((cell, idx) => {
          if (!showOutsideDays && cell.outside) {
            return <div key={idx} />
          }

          const isSunday   = cell.date.getDay() === 0
          const isToday    = sameDay(cell.date, today)
          const isSelected = sameDay(cell.date, selected)

          const textColor = isSelected
            ? 'hsl(var(--primary-foreground))'
            : isToday
            ? 'hsl(var(--accent-foreground))'
            : isSunday
            ? cell.outside ? '#fca5a5' : '#ef4444'
            : cell.outside
            ? 'hsl(var(--muted-foreground))'
            : 'inherit'

          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3px 0',
              }}
            >
              <div
                className="cal-day-cell"
                onClick={() => { if (mode === 'single') onSelect?.(cell.date) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: isToday || isSelected ? 600 : 400,
                  background: isSelected
                    ? 'hsl(var(--primary))'
                    : isToday
                    ? 'hsl(var(--accent))'
                    : 'transparent',
                  color: textColor,
                  opacity: cell.outside ? 0.4 : 1,
                  transition: 'background 0.15s',
                  position: 'relative',
                  flexShrink: 0,
                }}
                onMouseEnter={e => {
                  if (!isSelected && !isToday)
                    e.currentTarget.style.background = 'hsl(var(--accent))'
                }}
                onMouseLeave={e => {
                  if (!isSelected && !isToday)
                    e.currentTarget.style.background = 'transparent'
                }}
              >
                {ExternalDayContent
                  ? <ExternalDayContent date={cell.date} activeModifiers={{ selected: isSelected, today: isToday, outside: cell.outside }} />
                  : cell.date.getDate()
                }
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

Calendar.displayName = "Calendar"
export { Calendar }